import { withMask } from 'use-mask-input'

import type { ComponentProps } from 'react'
import React, { useCallback } from 'react'

import type { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form'

import { logger } from '@/lib/logger'

import { FormField } from '@/components/ui/form'
import { Input } from '@/components/ui/input'

import { FormFieldWrapper } from './FormFieldWrapper'

// Export the interface so it can be used in other components
export interface MaskedInputFieldProps<T extends FieldValues> {
  form: UseFormReturn<T>
  name: FieldPath<T>
  label: string
  tooltip?: string
  desc?: string
  mask?: string
  inputProps?: ComponentProps<typeof Input>
  placeholder?: string
  onValue?: (val: string) => string
  required?: boolean
  disabled?: boolean
  className?: string
  type?: React.HTMLInputTypeAttribute
  autoComplete?: React.HTMLInputAutoCompleteAttribute
}

export function MaskedInputField<T extends FieldValues>({
  form,
  name,
  label,
  tooltip,
  desc,
  mask,
  inputProps,
  placeholder,
  onValue,
  required = false,
  disabled = false,
  className,
  type = 'text',
  autoComplete,
}: MaskedInputFieldProps<T>) {
  const fieldState = form.getFieldState(name)

  // Safely create masked ref with error handling
  const createMaskedRef = useCallback((mask: string) => {
    try {
      return withMask(mask, {
        placeholder: '',
        showMaskOnHover: false,
      })
    } catch (error) {
      logger.warn('Failed to create masked input:', error)
      return undefined
    }
  }, [])

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormFieldWrapper label={label} tooltip={tooltip} desc={desc} required={required}>
          <Input
            {...field}
            {...inputProps}
            type={type}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            autoComplete={autoComplete}
            className={className}
            aria-invalid={!!fieldState.error}
            aria-required={required}
            value={(field.value as string | undefined | null) ?? ''}
            ref={mask ? createMaskedRef(mask) : field.ref}
            onChange={(e) => {
              let val = e.target.value
              if (onValue) val = onValue(val)
              field.onChange(val)
            }}
          />
        </FormFieldWrapper>
      )}
    />
  )
}

// Memoized version for better performance
export const MemoizedMaskedInputField = React.memo(MaskedInputField) as typeof MaskedInputField
